import { parseAnchorValue } from "./anchor.js";
import { collectionKeyForType, splitNodePath, type GraphNode, type NeedleAddress } from "./address.js";
import { NEEDLE_ID_KEY } from "./id.js";
import { STAMPED_COLLECTIONS, type GltfJson } from "./gltf.js";

export type GltfResolveResult = {
    type: string;
    index: number;
    element: Record<string, unknown>;
};

/**
 * Resolve an address inside glTF JSON. Same ladder as the live graph —
 * **anchor → id → path** (nodes) — with the anchor winning only when it matches
 * exactly one element. Returns null when nothing matches exactly (reconciliation is a
 * separate, explicit step — resolution never guesses).
 */
export function resolveInGltf(address: NeedleAddress, json: GltfJson, options?: ResolveOptions): GltfResolveResult | null {
    const report = options?.report;
    const searched: Array<readonly [string, string]> = address.type
        ? [[address.type, collectionKeyForType(address.type)]]
        : [...STAMPED_COLLECTIONS];

    let byAnchor: GltfResolveResult | null = null;
    if (address.anchor) {
        const matches: GltfResolveResult[] = [];
        for (const [type, key] of searched) {
            for (const found of collectInJsonCollection(json, key, el => parseAnchorValue(el.name as string | undefined) === address.anchor)) {
                matches.push({ type, index: found.index, element: found.element });
                if (matches.length > 1 && !report) break;
            }
            if (matches.length > 1 && !report) break;
        }
        if (matches.length === 1) byAnchor = matches[0];
        else if (matches.length > 1) report?.({ kind: "ambiguous-anchor", anchor: address.anchor, count: matches.length });
    }
    if (byAnchor && !report) return byAnchor;

    let byId: GltfResolveResult | null = null;
    if (address.id) {
        for (const [type, key] of searched) {
            const found = findInCollection(json, key, el => el.extras?.[NEEDLE_ID_KEY] === address.id);
            if (found) { byId = { type, index: found.index, element: found.element }; break; }
        }
    }

    if (byAnchor) {
        if (byId && (byId.type !== byAnchor.type || byId.index !== byAnchor.index)) {
            report?.({ kind: "conflict", preferred: "anchor", other: "id" });
        }
        return byAnchor;
    }
    if (byId) return byId;

    if (address.path !== undefined && (!address.type || address.type === "node")) {
        const index = walkGltfNamePath(json, splitNodePath(address.path));
        if (index !== null) {
            const nodes = json["nodes"] as Array<Record<string, unknown>>;
            return { type: "node", index, element: nodes[index] };
        }
    }
    return null;
}

/** which rung of the ladder produced (or could have produced) a match */
export type ResolutionRung = "id" | "anchor" | "path" | "name";

/**
 * Something a caller may want to show a human. Never an error — resolution still returns
 * its best answer; these say the answer was not the only defensible one.
 */
export type ResolutionIssue =
    /** an anchor matched several elements, so it could not outrank an exact key */
    | { kind: "ambiguous-anchor"; anchor: string; count: number }
    /** a name matched several elements in a flat collection */
    | { kind: "ambiguous-name"; name: string; count: number }
    /** two rungs resolved to DIFFERENT elements — the content changed underneath */
    | { kind: "conflict"; preferred: ResolutionRung; other: ResolutionRung };

export type ResolveOptions = {
    /**
     * Collect ambiguity/conflict evidence. OPT-IN because it costs extra traversals:
     * detecting that two rungs disagree means evaluating both instead of stopping at the
     * first hit, and resolution already walks from the root per address. The runtime
     * short-circuits and pays nothing; the editor asks for evidence so it can surface
     * drift to the user.
     */
    report?: (issue: ResolutionIssue) => void;
};

/**
 * Resolve an address inside a live scene graph (duck-typed — any three.js).
 *
 * Ladder: **anchor → id → path**. The anchor comes first because it is a deliberate,
 * human-authored declaration, while an id may be a derived value whose basis shifted when
 * the content changed. It only wins when it resolves UNIQUELY: anchors are labels and may
 * legally repeat, and an ambiguous label must never outrank an exact key.
 */
export function resolveInGraph(address: NeedleAddress, root: GraphNode, options?: ResolveOptions): GraphNode | null {
    const report = options?.report;

    let byAnchor: GraphNode | null = null;
    if (address.anchor) {
        // without a reporter, stop at two — "exactly one" vs "more than one" is all the
        // ladder needs, and the count only matters for the message
        const matches = collectInGraph(root, n => parseAnchorValue(n.name) === address.anchor, report ? Infinity : 2);
        if (matches.length === 1) byAnchor = matches[0];
        else if (matches.length > 1) report?.({ kind: "ambiguous-anchor", anchor: address.anchor, count: matches.length });
    }
    // fast path: nothing left to learn unless the caller wants the disagreement reported
    if (byAnchor && !report) return byAnchor;

    let byId: GraphNode | null = null;
    if (address.id) byId = findInGraph(root, n => n.userData?.[NEEDLE_ID_KEY] === address.id);

    if (byAnchor) {
        if (byId && byId !== byAnchor) report?.({ kind: "conflict", preferred: "anchor", other: "id" });
        return byAnchor;
    }
    if (byId) return byId;

    if (address.path !== undefined) {
        let current: GraphNode | null = root;
        for (const segment of splitNodePath(address.path)) {
            const matches: GraphNode[] = current.children?.filter(c => (c.name ?? "") === segment.name) ?? [];
            current = segment.occurrence !== undefined ? matches[segment.occurrence] ?? null : matches.length === 1 ? matches[0] : null;
            if (!current) break;
        }
        if (current && current !== root) return current;
    }
    return null;
}

/** duck-typed view of an element that is not a graph node (a material, texture, …) */
export type AddressableElement = {
    name?: string;
    userData?: Record<string, unknown>;
};

/**
 * Resolve an address against a FLAT collection of live elements — materials, textures and
 * anything else that carries a name and `userData` but is not part of the scene graph.
 *
 * Same ladder as the graph, minus `path` (there is no hierarchy to walk) plus `name`
 * as the final rung, since a flat collection has no positional fallback. This exists so
 * every element type resolves through ONE implementation: materials previously had their
 * own ladders, could not be anchored at all, and disagreed with each other about whether a
 * name match had to be unique.
 */
export function resolveInCollection<T extends AddressableElement>(address: NeedleAddress, elements: readonly T[], options?: ResolveOptions): T | null {
    const report = options?.report;

    let byAnchor: T | null = null;
    if (address.anchor) {
        const matches = elements.filter(e => parseAnchorValue(e.name) === address.anchor);
        if (matches.length === 1) byAnchor = matches[0];
        else if (matches.length > 1) report?.({ kind: "ambiguous-anchor", anchor: address.anchor, count: matches.length });
    }
    if (byAnchor && !report) return byAnchor;

    const byId = address.id ? elements.find(e => e.userData?.[NEEDLE_ID_KEY] === address.id) ?? null : null;

    if (byAnchor) {
        if (byId && byId !== byAnchor) report?.({ kind: "conflict", preferred: "anchor", other: "id" });
        return byAnchor;
    }
    if (byId) return byId;

    if (address.name) {
        const named = elements.filter(e => e.name === address.name);
        if (named.length === 1) return named[0];
        if (named.length > 1) report?.({ kind: "ambiguous-name", name: address.name, count: named.length });
    }
    return null;
}

export type ReconcileCandidate = {
    object: GraphNode;
    /** 0..1 — heuristic, for ranking and thresholds; 1.0 is reserved for exact resolution */
    confidence: number;
    reasons: string[];
};

/**
 * When exact resolution fails (the asset changed), propose candidates instead of
 * guessing: nodes matching the address's name, ranked by how much of the recorded
 * path still matches. Callers decide thresholds and whether to re-anchor.
 */
export function reconcileInGraph(address: NeedleAddress, root: GraphNode): ReconcileCandidate[] {
    const addressSegments = address.path !== undefined ? splitNodePath(address.path).map(segment => segment.name) : null;
    if (!address.name && !addressSegments?.length) return [];
    const targetName = address.name ?? addressSegments![addressSegments!.length - 1];

    const candidates: ReconcileCandidate[] = [];
    collectDescendants(root, (node, segments) => {
        if ((node.name ?? "") !== targetName) return;
        const reasons = [`name matches "${targetName}"`];
        let confidence = 0.5;
        if (addressSegments?.length) {
            const similarity = pathSuffixSimilarity(addressSegments, segments);
            confidence += 0.4 * similarity;
            if (similarity > 0) reasons.push(`path suffix similarity ${(similarity * 100).toFixed(0)}%`);
        }
        candidates.push({ object: node, confidence, reasons });
    });
    if (candidates.length === 1) {
        candidates[0].confidence = Math.min(0.95, candidates[0].confidence + 0.1);
        candidates[0].reasons.push("only candidate with this name");
    }
    return candidates.sort((a, b) => b.confidence - a.confidence);
}

/** every element of one glTF collection matching `predicate` */
function collectInJsonCollection(json: GltfJson, key: string, predicate: (el: Record<string, any>) => boolean): Array<{ index: number; element: Record<string, unknown> }> {
    const collection = json[key];
    if (!Array.isArray(collection)) return [];
    const found: Array<{ index: number; element: Record<string, unknown> }> = [];
    for (let i = 0; i < collection.length; i++) {
        const element = collection[i];
        if (element != null && typeof element === "object" && predicate(element)) {
            found.push({ index: i, element });
        }
    }
    return found;
}

function findInCollection(json: GltfJson, key: string, predicate: (el: Record<string, any>) => boolean): { index: number; element: Record<string, unknown> } | null {
    const collection = json[key];
    if (!Array.isArray(collection)) return null;
    for (let i = 0; i < collection.length; i++) {
        const element = collection[i];
        if (element != null && typeof element === "object" && predicate(element)) {
            return { index: i, element };
        }
    }
    return null;
}

function walkGltfNamePath(json: GltfJson, segments: Array<{ name: string; occurrence?: number }>): number | null {
    const nodes = json["nodes"];
    const scenes = json["scenes"];
    if (!Array.isArray(nodes) || !Array.isArray(scenes)) return null;
    // roots: the union of all scenes' root nodes
    let candidates: number[] = scenes.flatMap(s => (Array.isArray(s?.nodes) ? s.nodes : []));
    let match: number | null = null;
    for (const segment of segments) {
        const matches = [...new Set(candidates)].filter(i => (nodes[i]?.name ?? "") === segment.name);
        match = segment.occurrence !== undefined ? matches[segment.occurrence] ?? null : matches.length === 1 ? matches[0] : null;
        if (match === null) return null;
        const children = nodes[match]?.children;
        candidates = Array.isArray(children) ? children : [];
    }
    return match;
}

/** every node matching `predicate`, stopping once `limit` are found (cycle-safe) */
function collectInGraph(root: GraphNode, predicate: (node: GraphNode) => boolean, limit: number): GraphNode[] {
    const found: GraphNode[] = [];
    const visited = new Set<GraphNode>();
    const walk = (node: GraphNode) => {
        if (found.length >= limit || visited.has(node)) return;
        visited.add(node);
        if (predicate(node)) {
            found.push(node);
            if (found.length >= limit) return;
        }
        if (!node.children) return;
        for (let i = 0; i < node.children.length; i++) {
            const child = node.children[i];
            if (child != null) walk(child);
        }
    };
    walk(root);
    return found;
}

function findInGraph(root: GraphNode, predicate: (node: GraphNode) => boolean, visited = new Set<GraphNode>()): GraphNode | null {
    if (visited.has(root)) return null;
    visited.add(root);
    if (predicate(root)) return root;
    if (!root.children) return null;
    for (let i = 0; i < root.children.length; i++) {
        const child = root.children[i];
        if (child == null) continue;
        const found = findInGraph(child, predicate, visited);
        if (found) return found;
    }
    return null;
}

/** visit every descendant of root with its root-exclusive name path (including own name) */
function collectDescendants(root: GraphNode, visit: (node: GraphNode, segments: string[]) => void): void {
    const visited = new Set<GraphNode>();
    const walk = (node: GraphNode, segments: string[]) => {
        if (visited.has(node)) return;
        visited.add(node);
        if (node !== root) visit(node, segments);
        if (!node.children) return;
        for (let i = 0; i < node.children.length; i++) {
            const child = node.children[i];
            if (child == null) continue;
            walk(child, [...segments, child.name ?? ""]);
        }
    };
    walk(root, []);
}

/** how much of the expected path's tail matches the actual path's tail (0..1) */
function pathSuffixSimilarity(expected: string[], actual: string[]): number {
    let shared = 0;
    while (
        shared < expected.length &&
        shared < actual.length &&
        expected[expected.length - 1 - shared] === actual[actual.length - 1 - shared]
    ) {
        shared++;
    }
    return shared / Math.max(expected.length, actual.length);
}
